import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/use-toast'
import { Search, Plus, Trash2, Crown, Loader2, ChevronDown, Check, RefreshCw, HelpCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

const EDGE_FUNCTION_URL = import.meta.env.VITE_EDGE_FUNCTION_URL || 
  'https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1'

interface StripeProduct {
  id: string
  name: string
  description?: string
}

interface PremiumProduct {
  product_id: string
  product_name: string
  inserted_at: string
}

interface StripeProductCached {
  product_id: string
  product_name: string
  product_description?: string | null
  active: boolean
}

export default function PremiumProducts() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [catalog, setCatalog] = useState<StripeProductCached[]>([])
  const [premiumProducts, setPremiumProducts] = useState<PremiumProduct[]>([])
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [manualProductId, setManualProductId] = useState('')
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  // Load premium products on mount
  useEffect(() => {
    loadPremiumProducts()
    loadStripeProducts()
  }, [])

  async function loadPremiumProducts() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      console.groupCollapsed('[PremiumProducts] loadPremiumProducts')
      console.debug('EDGE_FUNCTION_URL', EDGE_FUNCTION_URL)
      console.debug('user', { id: session.user?.id, email: session.user?.email })

      const res = await fetch(`${EDGE_FUNCTION_URL}/list-premium-products`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (!json.ok) throw new Error(json.error || 'Failed to load premium products')

      const rows: PremiumProduct[] = json.products || []
      setPremiumProducts(rows)
      setSelectedProductIds(new Set(rows.map(r => r.product_id)))
      console.debug('loaded premium products', { count: rows.length })
      console.groupEnd()
    } catch (error: any) {
      console.error('Error loading premium products:', error)
      toast({
        title: 'Error',
        description: 'Failed to load premium products',
        variant: 'destructive'
      })
      console.groupEnd?.()
    }
  }

  async function loadStripeProducts(q: string = '') {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const url = new URL(`${EDGE_FUNCTION_URL}/list-stripe-products`)
      if (q) url.searchParams.set('q', q)

      console.groupCollapsed('[PremiumProducts] loadStripeProducts')
      console.debug('url', url.toString())
      console.debug('query', q)
      console.debug('user', { id: session.user?.id, email: session.user?.email })

      const res = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })
      if (!res.ok) {
        const errText = await res.text().catch(() => '')
        console.error('EF loadStripeProducts failed', { status: res.status, body: errText })
        throw new Error(`HTTP ${res.status}`)
      }
      const json = await res.json()
      if (!json.ok) throw new Error(json.error || 'Failed to load Stripe products')
      setCatalog(json.products || [])
      console.debug('loaded stripe products', { count: (json.products || []).length })
      console.groupEnd()
    } catch (error: any) {
      console.error('Error loading Stripe products:', error)
      toast({ title: 'Error', description: 'Failed to load Stripe products', variant: 'destructive' })
      console.groupEnd?.()
    }
  }

  async function refreshStripeProducts() {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')
      const url = `${EDGE_FUNCTION_URL}/sync-stripe-products`
      console.groupCollapsed('[PremiumProducts] refreshStripeProducts')
      console.debug('url', url)
      console.debug('user', { id: session.user?.id, email: session.user?.email })
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })
      if (!res.ok) {
        const errText = await res.text().catch(() => '')
        console.error('EF sync-stripe-products failed', { status: res.status, body: errText })
        throw new Error(`HTTP ${res.status}`)
      }
      const json = await res.json()
      if (!json.ok) throw new Error(json.error || 'Failed to sync Stripe products')
      await loadStripeProducts(searchQuery)
      toast({ title: 'Refreshed', description: `Fetched ${json.fetched} products from Stripe` })
      console.debug('sync result', json)
      console.groupEnd()
    } catch (error: any) {
      console.error('Error refreshing products:', error)
      toast({ title: 'Refresh failed', description: error.message, variant: 'destructive' })
    } finally {
      setLoading(false)
      console.groupEnd?.()
    }
  }

  async function addManualProduct() {
    const pid = manualProductId.trim()
    if (!pid) {
      toast({ title: 'Missing product ID', description: 'Enter a Stripe product ID (prod_...)', variant: 'destructive' })
      return
    }

    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')
      console.groupCollapsed('[PremiumProducts] addManualProduct')
      console.debug('productId', pid)
      console.debug('EDGE_FUNCTION_URL', EDGE_FUNCTION_URL)
      console.debug('user', { id: session.user?.id, email: session.user?.email })
      const response = await fetch(
        `${EDGE_FUNCTION_URL}/save-premium-products`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ productIds: [pid] })
        }
      )
      if (!response.ok) {
        const errText = await response.text().catch(() => '')
        console.error('EF save-premium-products failed', { status: response.status, body: errText })
        throw new Error(`HTTP ${response.status}`)
      }
      const data = await response.json()
      if (!data.ok) throw new Error(data.error || 'Failed to save product')

      toast({ title: 'Added', description: `Product ${pid} added as premium` })
      setManualProductId('')
      await loadPremiumProducts()
      console.debug('added product ok')
      console.groupEnd()
    } catch (error: any) {
      console.error('Error adding product:', error)
      toast({ title: 'Add failed', description: error.message, variant: 'destructive' })
    } finally {
      setLoading(false)
      console.groupEnd?.()
    }
  }

  // Toggle selection in multi-select
  function toggleSelected(id: string) {
    const next = new Set(selectedProductIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedProductIds(next)
  }

  async function saveSelection() {
    setLoading(true)
    try {
      const current = new Set(premiumProducts.map(p => p.product_id))
      const toAdd = Array.from(selectedProductIds).filter(id => !current.has(id))
      const toRemove = Array.from(current).filter(id => !selectedProductIds.has(id))

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      console.groupCollapsed('[PremiumProducts] saveSelection')
      console.debug('toAdd', toAdd)
      console.debug('toRemove', toRemove)
      console.debug('user', { id: session.user?.id, email: session.user?.email })

      // Add new selections
      if (toAdd.length > 0) {
        const res = await fetch(`${EDGE_FUNCTION_URL}/save-premium-products`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ productIds: toAdd })
        })
        if (!res.ok) {
          const errText = await res.text().catch(() => '')
          console.error('EF save-premium-products failed', { status: res.status, body: errText })
          throw new Error(`HTTP ${res.status}`)
        }
        const json = await res.json()
        if (!json.ok) throw new Error(json.error || 'Failed to add products')
        console.debug('added products ok', json)
      }

      // Remove deselected
      for (const id of toRemove) {
        const res = await fetch(`${EDGE_FUNCTION_URL}/remove-premium-product`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ productId: id })
        })
        if (!res.ok) {
          const errText = await res.text().catch(() => '')
          console.error('EF remove-premium-product failed', { id, status: res.status, body: errText })
          throw new Error(`HTTP ${res.status}`)
        }
        const json = await res.json()
        if (!json.ok) throw new Error(json.error || 'Failed to remove product')
        console.debug('removed product ok', { id })
      }

      await loadPremiumProducts()
      toast({ title: 'Saved', description: 'Premium product selection updated' })
      setOpen(false)
      console.groupEnd()
    } catch (error: any) {
      console.error('Error saving selection:', error)
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' })
    } finally {
      setLoading(false)
      console.groupEnd?.()
    }
  }

  async function removePremiumProduct(productId: string) {
    if (!confirm('Remove this product from premium list?')) {
      return
    }

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      console.groupCollapsed('[PremiumProducts] removePremiumProduct')
      console.debug('productId', productId)
      console.debug('user', { id: session.user?.id, email: session.user?.email })
      const res = await fetch(`${EDGE_FUNCTION_URL}/remove-premium-product`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId })
      })

      if (!res.ok) {
        const errText = await res.text().catch(() => '')
        console.error('EF remove-premium-product failed', { status: res.status, body: errText })
        throw new Error(`HTTP ${res.status}`)
      }
      const json = await res.json()
      if (!json.ok) throw new Error(json.error || 'Failed to remove product')

      toast({
        title: 'Removed',
        description: 'Product removed from premium list'
      })

      await loadPremiumProducts()
      console.debug('removed product ok')
      console.groupEnd()
    } catch (error: any) {
      console.error('Error removing product:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove product',
        variant: 'destructive'
      })
      console.groupEnd?.()
    }
  }

  function isSelected(id: string) {
    return selectedProductIds.has(id)
  }

  return (
    <>
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Does "Sync Products" Do?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="space-y-4 text-base">
            <div>
              <h4 className="font-semibold text-foreground mb-2">In Simple Terms:</h4>
              <p>
                The "Sync Products" (or "Refresh") button downloads your product list from Stripe and saves it to your app's database. 
                This lets you see all your Stripe products here in the admin panel.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">Why Do This?</h4>
              <p>
                Your app needs to know which products exist in Stripe so you can select which ones should give users "premium" access. 
                Without syncing, your app wouldn't know what products you have available in Stripe.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-foreground mb-2">When Should I Use It?</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>When you first set up your app</li>
                <li>After you create new products in Stripe</li>
                <li>If the product list looks outdated</li>
              </ul>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold text-foreground mb-2">The Process:</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Connects to your Stripe account</li>
                <li>Downloads all your products (name, ID, description, status)</li>
                <li>Saves them to your app's database</li>
                <li>Now you can select which products grant premium access</li>
              </ol>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
      
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Crown className="h-8 w-8 text-yellow-500" />
          <h1 className="text-3xl font-bold">Premium Products</h1>
        </div>
        <p className="text-muted-foreground">
          Manage which Stripe products grant premium access. Users with active subscriptions to these products get premium features.
        </p>
      </div>

      {/* Current Premium Products */}
      <Card>
        <CardHeader>
          <CardTitle>Current Premium Products</CardTitle>
          <CardDescription>
            Products that currently grant premium access ({premiumProducts.length})
          </CardDescription>
        </CardHeader>
        <CardContent>
          {premiumProducts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No premium products configured yet
            </div>
          ) : (
            <div className="space-y-2">
              {premiumProducts.map((product) => (
                <div
                  key={product.product_id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">{product.product_name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {product.product_id}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePremiumProduct(product.product_id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Select Premium Products */}
      <Card>
        <CardHeader>
          <CardTitle>Select Premium Products</CardTitle>
          <CardDescription>
            Choose which Stripe products grant premium access. Use search to filter and Refresh to sync new products from Stripe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between w-full md:w-[480px]">
                  <span className="truncate">
                    {selectedProductIds.size > 0
                      ? `${selectedProductIds.size} product(s) selected`
                      : 'Select products...'}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[480px] p-0">
                <Command filter={(value, search) => value.includes(search.toLowerCase()) ? 1 : 0}>
                  <div className="px-2 py-2">
                    <CommandInput
                      placeholder="Search products by name or ID..."
                      value={searchQuery}
                      onValueChange={(v) => {
                        setSearchQuery(v)
                        // Server-side filter
                        const t = setTimeout(() => loadStripeProducts(v), 200)
                      }}
                    />
                  </div>
                  <CommandEmpty>No products found.</CommandEmpty>
                  <CommandList className="max-h-72">
                    <CommandGroup>
                      {catalog.map((p) => (
                        <CommandItem
                          key={p.product_id}
                          value={`${p.product_name} ${p.product_id}`.toLowerCase()}
                          onSelect={() => toggleSelected(p.product_id)}
                        >
                          <Check className={`mr-2 h-4 w-4 ${isSelected(p.product_id) ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{p.product_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{p.product_id}</div>
                          </div>
                          {!p.active && (
                            <Badge variant="outline" className="ml-auto">inactive</Badge>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <div className="flex gap-2">
              <Button variant="outline" onClick={refreshStripeProducts} disabled={loading}>
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Refreshing</>) : (<><RefreshCw className="mr-2 h-4 w-4" /> Refresh</>)}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setHelpOpen(true)}>
                <HelpCircle className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={saveSelection} disabled={loading}>
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</>) : (<><Plus className="mr-2 h-4 w-4" /> Save Selection</>)}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  )
}
